import { ComponentRef, DOCUMENT, Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Interactable } from '@interactjs/core/Interactable';
import interact from 'interactjs';
import { assert } from 'src/app/core/assert/assert';
import {
  DragActiveCSSClass,
  DragActiveEntryCSSClass,
  GapAttribute,
} from '../../../../../core/constants-repository/sortable-rows.constants';
import { TodoEntryModel } from '../../../../../core/models/todo-planner/todo-entry.model';
import { SortableEntriesDefaultAnimationDuration } from '../../../../../core/services/features/sortable-elements/sortable-elements.constants';
import { SortableEntriesService } from '../../../../../core/services/features/sortable-elements/sortable-entries.service';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';
import { DragOrResizeInteraction } from '../../../../../shared/data-types/interact.types';
import { WINDOW } from '../../../../calendar/util/browser-window-ref';
import { DateTimeHelper } from '../../../../calendar/util/date-time-helper';
import { TodoPlannerService } from '../../../services/todo-planner.service';
import {
  TodoContainerDateAttribute,
  TodoEntryWrapperCSSClass,
} from '../../../utils/todo-planner.constants';
import { TodoEntryComponentHandlerService } from '../todo-entry-component-handler.service';
import { TodoEntryComponent } from '../../todo-entry.component';
import { TodoEntryCrudService } from '../todo-entry-crud.service';
import { TodoEntryService } from '../todo-entry.service';
import {
  TodoEntryDragSession,
  TodoEntrySharedDragAndDropService,
} from './todo-entry-shared-drag-and-drop.service';

export enum Gap {
  None,
  Above,
  Below,
}

@Injectable({ providedIn: 'root' })
export class TodoEntryDragService implements OnDestroy {
  public interaction = DragOrResizeInteraction.None;

  // DI
  private readonly render = inject(ShiveRenderService);
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly todoEntrySharedDAndDService = inject(TodoEntrySharedDragAndDropService);
  private readonly componentHandler = inject(TodoEntryComponentHandlerService);
  private readonly sortable = inject(SortableEntriesService);
  private readonly todoEntryService = inject(TodoEntryService);
  private readonly crud = inject(TodoEntryCrudService);
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly window = inject<Window>(WINDOW);

  // interactables
  private readonly interactables = new WeakMap<HTMLElement, Interactable>();

  constructor() {
    // global setting; if you want, move this to app bootstrap
    interact.dynamicDrop(true);
  }

  ngOnDestroy(): void {
    this.endSession(true);
  }

  /**
   * Must be called for every draggable entry instance.
   * Safe to call repeatedly: we tear-down any previous interactable on that element.
   */
  public initializeDragAndDrop(
    draggedComponentRef: ComponentRef<TodoEntryComponent>,
    restrictionElement: HTMLElement,
  ): void {
    const wrapper = draggedComponentRef.instance.todoEntryWrapper().nativeElement as HTMLElement;

    // Ensure no duplicate handlers on same element
    this.unsetInteractable(wrapper);
    this.ngZone.runOutsideAngular(() => {
      const interactable = interact(wrapper)
        .draggable({
          manualStart: true,
          modifiers: [
            interact.modifiers.restrictRect({
              restriction: () => restrictionElement.getBoundingClientRect(),
              endOnly: false,
            }),
          ],
          listeners: {
            start: (evt) => this.onDragStart(evt, draggedComponentRef, restrictionElement),
            move: (evt) => this.onDragMove(evt),
            end: () => this.onDragEnd(),
          },
        })
        .dropzone({
          overlap: 0.6,
          ondragenter: (evt) => this.onDragEnterScheduled(evt),
        })
        .styleCursor(false);

      this.interactables.set(wrapper, interactable);
    });
  }

  public firstTouchAfterLaneSwitch(): void {
    const firstTouchTodoEntry = this.todoEntrySharedDAndDService.session.firstTouchedPlaceholderRef;

    if (!firstTouchTodoEntry) {
      return;
    }

    const firstTouchWrapper = firstTouchTodoEntry.instance.todoEntryWrapper()
      .nativeElement as HTMLElement;

    this.initFirstLaneOverlapFromWrapper(firstTouchWrapper);
  }

  public initFirstLaneOverlapFromWrapper(wrapperEl: HTMLElement): void {
    const s = this.todoEntrySharedDAndDService.session;
    if (!s) {
      return;
    }
    this.initFirstLaneOverlap(s, wrapperEl);
  }

  // ---------- Session lifecycle ----------

  private onDragStart(
    event: Interact.DragEvent,
    draggedCompRef: ComponentRef<TodoEntryComponent>,
    restrictionEl: HTMLElement,
  ): void {
    const targetEl = event.target as HTMLElement;

    // cancel any late cleanup from previous drag
    this.endSession(false);

    this.interaction = DragOrResizeInteraction.Drag;

    const selectedId = Number(this.render.getAttribute(targetEl, 'id'));
    const selectedModel = this.crud.selectTodoEntry(selectedId);
    assert(typeof selectedModel !== 'undefined');

    const entryHeight = draggedCompRef.instance.todoEntryWrapper().nativeElement.clientHeight || 0;

    const currentLaneDate = DateTimeHelper.format(selectedModel.start);

    // ghost = current element’s componentRef (based on your existing behavior)
    const ghostRef = selectedModel.componentRef;
    assert(!!ghostRef);

    const firstTouchedPlaceholderRef = ghostRef;

    // cache container element once per drag
    const container = this.todoPlannerService.getTodoEntryWrapper(
      firstTouchedPlaceholderRef.instance.todoEntryModel(),
    );
    assert(typeof container !== 'undefined');
    const containerEl = container.elementRef.nativeElement as HTMLElement;

    this.todoEntrySharedDAndDService.session = {
      draggedCompRef,
      draggedEl: draggedCompRef.instance.todoEntryWrapper().nativeElement as HTMLElement,
      restrictionEl,
      ghostRef,
      firstTouchedPlaceholderRef,
      oldContainerElement: containerEl,
      currentContainerEl: containerEl,
      currentLaneDate,
      entryHeight,
      curSortedIndex: -1,
      cleanupTimeoutId: null,
      dragEnterRafId: 0,
      dragInProcess: true,
    };

    // CSS classes
    this.render.addClass(this.document.body, DragActiveCSSClass);
    this.render.addClass(this.document.body, 'noselect');
    this.render.addClass(targetEl, DragActiveEntryCSSClass);

    // optionally store current lane on session instead of public field
  }

  private onDragEnd(): void {
    const s = this.todoEntrySharedDAndDService.session;
    if (!s) {
      return;
    }
    this.interaction = DragOrResizeInteraction.None;

    // Insert dragged element into new lane/index.
    const toInsert = this.createToBeInsertedTodoEntry(s);

    // Remove original element.
    const draggedTodoEntry = s.draggedCompRef.instance.todoEntryModel();
    this.crud.removeTodoEntry(draggedTodoEntry);

    // If onDragEnter() was not called because user moved the to-do entry into another lane, reset old lane here.
    this.todoEntrySharedDAndDService.resetOldLane();
    const originalLaneDate = draggedTodoEntry.start;
    s.draggedCompRef = null;
    s.draggedEl = null;

    // Update sorted indices of original lane's to-do entries.
    this.todoEntryService.updateSortedIndices(originalLaneDate, draggedTodoEntry);
    // this.sendToServer(originalLaneDate);

    // Remove ghost
    this.componentHandler.removeComponent(s.ghostRef);

    this.todoEntryService.resetDataAttributesAndTransform(toInsert);

    // Add entry
    this.crud.addTodoEntry(toInsert, true);

    // Reset transform & update indices in target lane
    this.todoEntryService.updateSortedIndices(toInsert.start, toInsert);

    // cleanup classes after animation duration
    s.cleanupTimeoutId = this.window.setTimeout(() => {
      this.render.removeClass(this.document.body, DragActiveCSSClass);
      this.render.removeClass(this.document.body, 'noselect');
      this.endSession(false);
    }, SortableEntriesDefaultAnimationDuration);

    s.dragInProcess = false;

    // Send both lanes if different, otherwise just one
    if (DateTimeHelper.isSameDay(originalLaneDate, toInsert.start)) {
      this.todoEntryService.sendToServer(toInsert.start);
    } else {
      this.todoEntryService.sendMultipleLanesToServer([originalLaneDate, toInsert.start]);
    }
  }

  private endSession(removeClasses: boolean): void {
    const s = this.todoEntrySharedDAndDService.session;
    if (!s) {
      return;
    }

    if (s.dragEnterRafId) {
      cancelAnimationFrame(s.dragEnterRafId);
    }

    if (s.cleanupTimeoutId) {
      clearTimeout(s.cleanupTimeoutId);
    }

    if (removeClasses) {
      this.render.removeClass(this.document.body, DragActiveCSSClass);
      this.render.removeClass(this.document.body, 'noselect');
    }

    this.todoEntrySharedDAndDService.session = null;
  }

  // ---------- Hot path handlers ----------

  private onDragMove(event: Interact.DragEvent): void {
    const s = this.todoEntrySharedDAndDService.session;
    if (!s) {
      return;
    }

    if (!event.dx && !event.dy) {
      return;
    }

    const el = event.target as HTMLElement;

    const x = (Number(el.dataset.x) || 0) + event.dx;
    const y = (Number(el.dataset.y) || 0) + event.dy;

    this.render.moveElement(el, x, y);

    el.dataset.x = String(x);
    el.dataset.y = String(y);
  }

  private onDragEnterScheduled(event: Interact.DropEvent): void {
    const s = this.todoEntrySharedDAndDService.session;
    if (!s) {
      return;
    }

    if (s.dragEnterRafId) {
      return;
    }
    s.dragEnterRafId = requestAnimationFrame(() => {
      s.dragEnterRafId = 0;
      this.onDragEnter(event);
    });
  }

  private onDragEnter(event: Interact.DropEvent): void {
    const s = this.todoEntrySharedDAndDService.session;
    if (!s?.dragInProcess) {
      return;
    }

    let targetDropEntry = event.target as HTMLDivElement;

    // first overlap init (placeholder already set to ghost at start in this refactor)
    if (this.initFirstLaneOverlap(s, targetDropEntry)) {
      return;
    }

    //* ****  subsequent overlaps: swap one at a time

    // Check if we switched lane before onDropEnter was called.
    if (this.switchedLaneBeforeOnDropEnter(targetDropEntry, s.currentContainerEl)) {
      this.handleLaneSwitchBeforeOnDropEnter(event, s);
      targetDropEntry = this.todoEntrySharedDAndDService.hasFirstTouchPlaceholder()
        ? s.firstTouchedPlaceholderRef.instance.todoEntryWrapper().nativeElement
        : targetDropEntry;
    }

    const delta = this.sortable.moveElementUpAndDown(
      targetDropEntry,
      s.currentContainerEl,
      s.curSortedIndex,
      'div',
    );

    s.curSortedIndex -= delta;
    this.sortable.setSortedIndex(s.draggedEl, s.curSortedIndex);
  }

  // ---------- Logic handlers ----------
  private initFirstLaneOverlap(s: TodoEntryDragSession, targetDropEntry: HTMLElement): boolean {
    // If a user moves the element very fast between lanes, it can happen that onDropEnter was not fired yet and therefore,
    //  firstTouchPlaceholder is null.
    if (!this.todoEntrySharedDAndDService.hasFirstTouchPlaceholder()) {
      return;
    }

    const placeholderWrapper =
      s.firstTouchedPlaceholderRef.instance.todoEntryWrapper().nativeElement;

    const gapType = Number(this.render.getAttribute(placeholderWrapper, GapAttribute)) as Gap;
    const targetId = Number(this.render.getAttribute(targetDropEntry, 'id'));
    const placeholderId = Number(this.render.getAttribute(placeholderWrapper, 'id'));

    if (targetId === placeholderId && gapType === Gap.None) {
      s.curSortedIndex = this.sortable.getSortedIndex(placeholderWrapper);
      this.updateStartingEntrySortedIndexAndGapType(s);

      this.moveSubsequentSiblingsDown(s, s.currentContainerEl);
      return true;
    }

    return false;
  }

  private updateStartingEntrySortedIndexAndGapType(s: TodoEntryDragSession): void {
    assert(!!s.firstTouchedPlaceholderRef);

    const placeholderEl = s.firstTouchedPlaceholderRef.instance.todoEntryWrapper().nativeElement;
    const placeholderY = placeholderEl.getBoundingClientRect().y;

    const draggingY = s.draggedEl.getBoundingClientRect().y;

    let gapType: Gap;

    if (draggingY > placeholderY) {
      gapType = Gap.Below;
      s.curSortedIndex++;
      this.sortable.setSortedIndex(s.draggedEl, s.curSortedIndex);
    } else {
      gapType = Gap.Above;
    }

    this.render.setAttribute(placeholderEl, GapAttribute, String(gapType));
  }

  private moveSubsequentSiblingsDown(s: TodoEntryDragSession, containerEl: HTMLElement): void {
    const selector = `.${TodoEntryWrapperCSSClass}:not(.${DragActiveEntryCSSClass})`;
    const nodes = containerEl.querySelectorAll<HTMLElement>(selector);

    const list: { el: HTMLElement; idx: number }[] = [];
    for (const el of Array.from(nodes)) {
      const idx = this.sortable.getSortedIndex(el);
      if (Number.isFinite(idx) && idx >= s.curSortedIndex) {
        list.push({ el, idx });
      }
    }

    for (const item of list) {
      this.sortable.updateAndAnimateEntries(item.el, item.idx + 1, s.entryHeight);
    }

    // Animate add button, if it participates
    const addTodoBtn = containerEl.nextElementSibling as HTMLElement | null;
    if (addTodoBtn) {
      const btnIdx = this.sortable.getSortedIndex(addTodoBtn);
      this.sortable.updateAndAnimateEntries(addTodoBtn, btnIdx + 1, s.entryHeight);
    }
  }

  private createToBeInsertedTodoEntry(s: TodoEntryDragSession): TodoEntryModel {
    const src = s.draggedCompRef.instance.todoEntryModel();
    const next = new TodoEntryModel();
    Object.assign(next, src);

    next.start = new Date(s.currentLaneDate);
    next.end = new Date(s.currentLaneDate);

    const ghostModel = s.ghostRef.instance.todoEntryModel();
    const ghostSameLane = DateTimeHelper.isSameDay(ghostModel.start, next.start);
    const ghostIsPredecessor = ghostSameLane && ghostModel.order < s.curSortedIndex;

    next.order = ghostIsPredecessor ? s.curSortedIndex - 1 : s.curSortedIndex;

    return next;
  }

  // ---------- Interact cleanup helpers ----------
  private unsetInteractable(el: HTMLElement): void {
    const existing = this.interactables.get(el);
    if (!existing) {
      return;
    }

    try {
      existing.unset();
    } catch {
      // ignore; interactjs can throw if already unset in some cases
    } finally {
      this.interactables.delete(el);
    }
  }

  private switchedLaneBeforeOnDropEnter(
    targetDropEntry: HTMLDivElement,
    currentContainerEl: HTMLElement,
  ): boolean {
    const newContainer = targetDropEntry.parentElement.parentElement;
    this.todoEntrySharedDAndDService.setContainer(newContainer);

    this.todoEntrySharedDAndDService.setContainer(currentContainerEl, true);

    return this.todoEntrySharedDAndDService.switchedLane();
  }

  private handleLaneSwitchBeforeOnDropEnter(
    event: Interact.DropEvent,
    s: TodoEntryDragSession,
  ): void {
    const targetDragEntry = event.relatedTarget as HTMLElement;

    const laneDate = this.shiveRenderService.getAttribute(
      s.currentContainerEl,
      TodoContainerDateAttribute,
    );
    const wrapper = this.todoPlannerService.getTodoEntryWrapper(new Date(laneDate));
    assert(typeof wrapper !== 'undefined');
    const wrapperEl = wrapper.elementRef.nativeElement as HTMLElement;
    this.todoEntrySharedDAndDService.handleLaneSwitch(
      targetDragEntry,
      s.currentContainerEl,
      wrapperEl,
    );
    this.firstTouchAfterLaneSwitch();
  }
}
